import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import axios from 'axios';

const DEFAULT_LIMIT = 50;

const SEARCH_FIELDS = [
  { key: 'feature_id',    label: 'Feature ID' },
  { key: 'major_feature', label: 'Major Feature' },
  { key: 'sub_feature',   label: 'Sub Feature' },
  { key: 'q',             label: 'Description (fuzzy)' },
  { key: 'repo',          label: 'Repo Name' },
  { key: 'glob',          label: 'Glob Pattern' },
];

// Product ID filter is handled by a separate dropdown, not the text search

function IconPlus() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
      strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  );
}

function IconSearch() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
  );
}

function IconChevronLeft() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6"/>
    </svg>
  );
}

function IconChevronRight() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6"/>
    </svg>
  );
}

function IconSheets() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2"/>
      <line x1="3" y1="9" x2="21" y2="9"/>
      <line x1="3" y1="15" x2="21" y2="15"/>
      <line x1="9" y1="3" x2="9" y2="21"/>
    </svg>
  );
}

function IconTrash() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
      <path d="M10 11v6"/><path d="M14 11v6"/>
      <path d="M9 6V4h6v2"/>
    </svg>
  );
}

export default function FeatureList() {
  const navigate = useNavigate();

  // Search state
  const [searchField, setSearchField] = useState('feature_id');
  const [searchValue, setSearchValue] = useState('');
  const [activeFilters, setActiveFilters] = useState({});
  const [productFilter, setProductFilter] = useState('');
  const [unmappedOnly, setUnmappedOnly]   = useState(false);
  const [products, setProducts]           = useState([]);

  // Data state
  const [rows, setRows]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [pages, setPages]         = useState(1);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [selectedIds, setSelectedIds]       = useState([]);
  const [allResultsSelected, setAllResultsSelected] = useState(false);
  const [bulkDeleting, setBulkDeleting]     = useState(false);

  const gridRef = useRef();

  const fetchData = useCallback(async (filters, pageNum, prodId, unmapped) => {
    setLoading(true);
    setError('');
    try {
      const params = { ...filters, page: pageNum, limit: DEFAULT_LIMIT };
      if (prodId) params.product_id = prodId;
      if (unmapped) params.unmapped = 1;
      Object.keys(params).forEach(k => { if (!params[k]) delete params[k]; });
      const res = await axios.get('/features', { params });
      setRows(res.data.data);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load features');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load products for filter dropdown
  useEffect(() => {
    axios.get('/products', { params: { limit: 200 } })
      .then(res => setProducts(res.data.data || []))
      .catch(() => {});
  }, []);

  // Initial load
  useEffect(() => {
    fetchData({}, 1, '', false);
  }, [fetchData]);

  function handleAddFilter() {
    if (!searchValue.trim()) return;
    const newFilters = { ...activeFilters, [searchField]: searchValue.trim() };
    setActiveFilters(newFilters);
    setSearchValue('');
    setPage(1);
    fetchData(newFilters, 1, productFilter, unmappedOnly);
  }

  function handleRemoveFilter(key) {
    const newFilters = { ...activeFilters };
    delete newFilters[key];
    setActiveFilters(newFilters);
    setPage(1);
    fetchData(newFilters, 1, productFilter, unmappedOnly);
  }

  function handleClearAll() {
    setActiveFilters({});
    setSearchValue('');
    setProductFilter('');
    setUnmappedOnly(false);
    setPage(1);
    fetchData({}, 1, '', false);
  }

  function handleProductFilterChange(e) {
    const val = e.target.value;
    setProductFilter(val);
    setPage(1);
    fetchData(activeFilters, 1, val, unmappedOnly);
  }

  function handleUnmappedToggle() {
    const next = !unmappedOnly;
    setUnmappedOnly(next);
    setPage(1);
    fetchData(activeFilters, 1, productFilter, next);
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') handleAddFilter();
  }

  function handlePageChange(newPage) {
    setPage(newPage);
    fetchData(activeFilters, newPage, productFilter, unmappedOnly);
  }

  function handleDelete(id) {
    if (!window.confirm('Delete this feature and all its code files?')) return;
    axios.delete(`/features/${id}`)
      .then(() => fetchData(activeFilters, page, productFilter, unmappedOnly))
      .catch(e => alert(e.response?.data?.error || 'Delete failed'));
  }

  function handleSelectionChanged() {
    const selected = gridRef.current?.api?.getSelectedRows() || [];
    setSelectedIds(selected.map(r => r.id));
    // If user deselects anything, drop out of "all results" mode
    if (selected.length < rows.length) setAllResultsSelected(false);
  }

  async function handleBulkDelete() {
    if (allResultsSelected) {
      await handleDeleteAllResults();
      return;
    }
    if (!selectedIds.length) return;
    if (!window.confirm(
      `Delete ${selectedIds.length} selected feature${selectedIds.length !== 1 ? 's' : ''} and all their code files? This cannot be undone.`
    )) return;
    setBulkDeleting(true);
    try {
      await axios.post('/features/bulk-delete', { ids: selectedIds });
      setSelectedIds([]);
      setAllResultsSelected(false);
      gridRef.current?.api?.deselectAll();
      await fetchData(activeFilters, page, productFilter, unmappedOnly);
    } catch (e) {
      alert(e.response?.data?.error || 'Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  }

  async function handleDeleteAllResults() {
    if (!window.confirm(
      `Delete ALL ${total.toLocaleString()} matching feature${total !== 1 ? 's' : ''} and their code files?\n\nThis affects every page of results and cannot be undone.`
    )) return;
    setBulkDeleting(true);
    try {
      const payload = { ...activeFilters };
      if (productFilter) payload.product_id = productFilter;
      if (unmappedOnly) payload.unmapped = 1;
      await axios.post('/features/delete-filtered', payload);
      setSelectedIds([]);
      setAllResultsSelected(false);
      gridRef.current?.api?.deselectAll();
      setPage(1);
      await fetchData(activeFilters, 1, productFilter, unmappedOnly);
    } catch (e) {
      alert(e.response?.data?.error || 'Delete failed');
    } finally {
      setBulkDeleting(false);
    }
  }

  const columnDefs = [
    {
      headerName: 'Product',
      field: 'product_name',
      width: 150,
      minWidth: 120,
      valueFormatter: p => p.value || '—',
      cellRenderer: params => params.value
        ? (
          <span style={{
            display: 'inline-block',
            background: 'rgba(99,102,241,0.12)',
            border: '1px solid rgba(99,102,241,0.35)',
            borderRadius: 4,
            padding: '2px 8px',
            fontSize: 12,
            color: 'var(--accent)',
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}>
            {params.value}
          </span>
        )
        : <span style={{ color: 'var(--text-muted)' }}>—</span>,
    },
    {
      headerName: 'Major Feature',
      field: 'major_feature',
      flex: 1,
      minWidth: 180,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      cellRenderer: params => (
        <button
          className="btn btn-ghost"
          style={{ padding: '2px 0', color: 'var(--accent)', fontWeight: 600, textAlign: 'left' }}
          onClick={() => navigate(`/features/${params.data.id}`)}
        >
          {params.value}
        </button>
      ),
    },
    { headerName: 'Sub Feature', field: 'sub_feature', flex: 1, minWidth: 140,
      valueFormatter: p => p.value || '—' },
    {
      headerName: 'Description',
      field: 'description',
      flex: 2,
      minWidth: 200,
      valueFormatter: p => p.value || '—',
      tooltipValueGetter: p => p.value || '',
      cellStyle: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        color: 'var(--text-secondary)',
      },
    },
    {
      headerName: 'Mapped Code Files & Repos',
      field: 'code_files',
      flex: 2,
      minWidth: 260,
      sortable: false,
      autoHeight: true,
      cellRenderer: params => {
        const files = params.value || [];
        if (files.length === 0) {
          return <span style={{ color: 'var(--text-muted)', lineHeight: '44px' }}>—</span>;
        }
        const visible = files.slice(0, 4);
        const extra = files.length - 4;
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '6px 0' }}>
            {visible.map((f, i) => (
              <span key={i} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                background: 'var(--accent-light)', border: '1px solid var(--accent)',
                borderRadius: 4, padding: '2px 7px', fontSize: 11, color: 'var(--accent)',
                whiteSpace: 'nowrap',
              }}>
                <span style={{ fontWeight: 600 }}>{f.repo}</span>
                <span style={{ color: 'var(--text-muted)', margin: '0 1px' }}>›</span>
                <span style={{ fontFamily: 'monospace', color: 'var(--text-secondary)' }}>{f.glob}</span>
              </span>
            ))}
            {extra > 0 && (
              <span style={{
                display: 'inline-flex', alignItems: 'center',
                fontSize: 11, color: 'var(--text-muted)', padding: '2px 4px',
              }}>
                +{extra} more
              </span>
            )}
          </div>
        );
      },
    },
    { headerName: 'Updated', field: 'updated_at', width: 130,
      valueFormatter: p => p.value ? new Date(p.value).toLocaleDateString() : '—' },
    {
      headerName: '',
      width: 120,
      sortable: false,
      cellRenderer: params => (
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            className="btn btn-ghost"
            style={{ fontSize: 12 }}
            onClick={() => navigate(`/features/${params.data.id}/edit`)}
          >
            Edit
          </button>
          <button
            className="btn btn-ghost"
            style={{ fontSize: 12, color: 'var(--danger)' }}
            onClick={() => handleDelete(params.data.id)}
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  const fieldLabel = SEARCH_FIELDS.find(f => f.key === searchField)?.label || searchField;
  const activeFilterKeys = Object.keys(activeFilters);
  const hasAnyFilter = activeFilterKeys.length > 0 || productFilter || unmappedOnly;

  return (
    <div>
      <div className="page-header">
        <h2>Product Features</h2>
        <p>Browse and manage all product features and their implementing code files</p>
      </div>

      <div className="page-body">
        {/* Toolbar */}
        <div className="toolbar">
          {/* Product filter */}
          <select
            className="form-input"
            style={{ width: 180 }}
            value={productFilter}
            onChange={handleProductFilterChange}
          >
            <option value="">All Products</option>
            {products.map(p => (
              <option key={p.id} value={String(p.id)}>{p.name}</option>
            ))}
          </select>

          {/* Field selector */}
          <select
            className="form-input"
            style={{ width: 160 }}
            value={searchField}
            onChange={e => setSearchField(e.target.value)}
          >
            {SEARCH_FIELDS.map(f => (
              <option key={f.key} value={f.key}>{f.label}</option>
            ))}
          </select>

          {/* Search input */}
          <input
            className="form-input search-input"
            placeholder={`Search by ${fieldLabel}…`}
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            onKeyDown={handleKeyDown}
          />

          <button className="btn btn-secondary" onClick={handleAddFilter}>
            <IconSearch /> Apply
          </button>

          {/* Unmapped-only toggle */}
          <button
            className={unmappedOnly ? 'btn btn-primary' : 'btn btn-secondary'}
            style={unmappedOnly ? { fontWeight: 700 } : {}}
            onClick={handleUnmappedToggle}
            title="Show only features with no code repository mapped"
          >
            No Repo Mapped
          </button>

          {hasAnyFilter && (
            <button className="btn btn-ghost" onClick={handleClearAll}
              style={{ color: 'var(--danger)' }}>
              Clear all
            </button>
          )}

          <div className="spacer" />

          <button className="btn btn-secondary" onClick={() => navigate('/features/import')}>
            <IconSheets /> Import from Sheets
          </button>

          <button className="btn btn-primary" onClick={() => navigate('/features/new')}>
            <IconPlus /> New Feature
          </button>
        </div>

        {/* Active filter chips */}
        {(activeFilterKeys.length > 0 || unmappedOnly) && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
            {activeFilterKeys.map(key => {
              const label = SEARCH_FIELDS.find(f => f.key === key)?.label || key;
              return (
                <div key={key} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: 'var(--accent-light)', border: '1px solid var(--accent)',
                  borderRadius: 20, padding: '3px 10px', fontSize: 12, color: 'var(--accent)',
                }}>
                  <span style={{ color: 'var(--text-muted)' }}>{label}:</span>
                  <strong>{activeFilters[key]}</strong>
                  <button
                    onClick={() => handleRemoveFilter(key)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer',
                      color: 'var(--accent)', padding: 0, fontSize: 14, lineHeight: 1 }}
                  >
                    ×
                  </button>
                </div>
              );
            })}
            {unmappedOnly && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: 'var(--accent-light)', border: '1px solid var(--accent)',
                borderRadius: 20, padding: '3px 10px', fontSize: 12, color: 'var(--accent)',
              }}>
                <span style={{ color: 'var(--text-muted)' }}>Filter:</span>
                <strong>No Repo Mapped</strong>
                <button
                  onClick={handleUnmappedToggle}
                  style={{ background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--accent)', padding: 0, fontSize: 14, lineHeight: 1 }}
                >
                  ×
                </button>
              </div>
            )}
          </div>
        )}

        {/* Bulk action bar */}
        {selectedIds.length > 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
            padding: '10px 16px', marginBottom: 6,
            background: 'var(--accent-light)', border: '1px solid var(--accent)',
            borderRadius: 'var(--radius)', fontSize: 13,
          }}>
            <span style={{ color: 'var(--accent)', fontWeight: 600 }}>
              {allResultsSelected
                ? `All ${total.toLocaleString()} matching features selected`
                : `${selectedIds.length} feature${selectedIds.length !== 1 ? 's' : ''} on this page selected`}
            </span>
            <button
              className="btn btn-danger"
              style={{ fontSize: 12, padding: '5px 12px' }}
              disabled={bulkDeleting}
              onClick={handleBulkDelete}
            >
              <IconTrash />
              {bulkDeleting
                ? 'Deleting…'
                : allResultsSelected
                  ? `Delete all ${total.toLocaleString()} results`
                  : `Delete ${selectedIds.length} selected`}
            </button>
            <button
              className="btn btn-ghost"
              style={{ fontSize: 12 }}
              onClick={() => { gridRef.current?.api?.deselectAll(); setSelectedIds([]); setAllResultsSelected(false); }}
            >
              Clear selection
            </button>
          </div>
        )}

        {/* Gmail-style "select all results" banner */}
        {selectedIds.length === rows.length && rows.length > 0 && total > rows.length && !allResultsSelected && (
          <div style={{
            padding: '8px 16px', marginBottom: 6, fontSize: 13,
            background: 'rgba(99,102,241,0.08)', borderRadius: 'var(--radius)',
            border: '1px solid var(--border)', color: 'var(--text-secondary)',
          }}>
            All {rows.length} features on this page are selected.{' '}
            <button
              className="btn btn-ghost"
              style={{ fontSize: 13, padding: '0 4px', color: 'var(--accent)', fontWeight: 600 }}
              onClick={() => setAllResultsSelected(true)}
            >
              Select all {total.toLocaleString()} matching features
            </button>
          </div>
        )}

        {error && <div className="error-msg">{error}</div>}

        {/* Grid */}
        <div style={{ position: 'relative' }}>
          {loading && (
            <div style={{
              position: 'absolute', inset: 0, zIndex: 10, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              background: 'rgba(15,23,42,0.5)', borderRadius: 'var(--radius-lg)',
            }}>
              <div className="spinner" />
            </div>
          )}
          <div
            className="ag-theme-alpine-dark"
            style={{ height: 520, width: '100%', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={rows}
              columnDefs={columnDefs}
              rowSelection="multiple"
              suppressRowClickSelection
              suppressMovableColumns
              suppressCellFocus
              headerHeight={40}
              tooltipShowDelay={300}
              defaultColDef={{ sortable: true, resizable: true }}
              onSelectionChanged={handleSelectionChanged}
              noRowsOverlayComponent={() => (
                <div className="empty-state">
                  <div className="icon">🗂</div>
                  <h3>No features found</h3>
                  <p>{activeFilterKeys.length > 0
                    ? 'Try adjusting your filters'
                    : 'Create your first feature to get started'}</p>
                </div>
              )}
            />
          </div>
        </div>

        {/* Pagination */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginTop: 16, fontSize: 13, color: 'var(--text-secondary)',
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {total === 0 ? 'No results' : `${(page - 1) * DEFAULT_LIMIT + 1}–${Math.min(page * DEFAULT_LIMIT, total)} of ${total.toLocaleString()} feature${total !== 1 ? 's' : ''}`}
            {total > 0 && (
              <button
                className="btn btn-ghost"
                style={{ fontSize: 12, color: 'var(--danger)', padding: '2px 6px' }}
                disabled={bulkDeleting}
                onClick={handleDeleteAllResults}
              >
                <IconTrash /> Delete all {total.toLocaleString()} results
              </button>
            )}
          </span>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <button
              className="btn btn-secondary"
              style={{ padding: '5px 10px' }}
              disabled={page <= 1}
              onClick={() => handlePageChange(page - 1)}
            >
              <IconChevronLeft />
            </button>
            <span style={{ minWidth: 80, textAlign: 'center' }}>
              Page {page} of {pages}
            </span>
            <button
              className="btn btn-secondary"
              style={{ padding: '5px 10px' }}
              disabled={page >= pages}
              onClick={() => handlePageChange(page + 1)}
            >
              <IconChevronRight />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
